import React, { useEffect, useState } from "react";
import { Button, Empty, Flex, Modal, notification, Select, Spin } from "antd";
import { getAllReports } from "api/reportTemplates.api";
import { addCandidatesToReport } from "api/report.api";
import { SvgIcons } from "Icon/SvgIcons";

interface Report {
    id: number;
    name: string;
    job_detail?: {
        id: number;
        title: string;
    };
    created: string;
}

interface AddToReportModalProps {
    open: boolean;
    onClose: () => void;
    selectedCandidates: number[];
    jobId: number | null;
}

const AddToReportModal: React.FC<AddToReportModalProps> = ({
    open,
    onClose,
    selectedCandidates,
    jobId,
}) => {
    const [reports, setReports] = useState<Report[]>([]);
    const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fetched, setFetched] = useState(false);

    useEffect(() => {
        if (open && jobId) {
            setFetched(false);
            setReports([]);
            fetchReports();
        }
        return () => {
            setSelectedReportId(null);
        };
    }, [open, jobId]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const data = await getAllReports({
                job: jobId as number,
                select_all: true,
            });
            const results = Array.isArray(data) ? data : (data?.results ?? []);
            setReports(results);
        } catch {
            notification.error({
                message: "Error",
                description: "Failed to load reports.",
            });
        } finally {
            setLoading(false);
            setFetched(true);
        }
    };

    const handleSubmit = async () => {
        if (!selectedReportId || !jobId) return;
        setSubmitting(true);
        try {
            await addCandidatesToReport(selectedReportId, {
                job: jobId,
                candidate_ids: selectedCandidates,
            });
            notification.success({
                message: "Success",
                description: `${selectedCandidates.length} candidate(s) added to the report.`,
            });
            onClose();
        } catch {
            notification.error({
                message: "Error",
                description: "Failed to add candidates to the report.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={
                <span className="inline-flex items-center gap-2">
                    <span className="[&_svg]:w-5 [&_svg]:h-5 text-primary">
                        {SvgIcons.fileIcon}
                    </span>
                    Add to Report
                </span>
            }
            open={open}
            onCancel={onClose}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={submitting}
                    disabled={!selectedReportId}
                    onClick={handleSubmit}
                >
                    Add to Report
                </Button>,
            ]}
        >
            <div className="flex flex-col gap-[6px] mt-4">
                <span className="text-text-secondary font-medium text-sm flex items-center gap-1">
                    Select Report <span className="text-primary">*</span>
                    {SvgIcons.questionSvg}
                </span>
                <div className="relative">
                    <span className="absolute top-1/2 left-2 -translate-y-1/2 z-10">
                        {SvgIcons.fileIcon}
                    </span>
                    {loading ? (
                        <Flex justify="center" className="py-4">
                            <Spin />
                        </Flex>
                    ) : fetched && reports.length === 0 ? (
                        <Empty
                            description="No reports found for this job"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ) : (
                        <Select
                            placeholder="Select Report"
                            optionFilterProp="label"
                            className="border rounded-md border-main-border w-full"
                            variant="borderless"
                            showSearch
                            filterOption={(input, option) =>
                                typeof option?.label === "string" &&
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            style={{
                                paddingLeft: "18px",
                                textTransform: "capitalize",
                            }}
                            options={reports.map((r) => ({
                                label: r.name,
                                value: r.id,
                            }))}
                            value={selectedReportId}
                            onChange={(val) => setSelectedReportId(val)}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default AddToReportModal;
